<template>
  <Transition name="briefing-fade">
    <div
      v-if="visible"
      class="briefing-backdrop"
      role="dialog"
      aria-modal="true"
      :aria-label="STRINGS.tutorial.briefing.title"
      @keydown.esc="onSkip"
    >
      <div class="briefing-panel" ref="panelRef">
        <!-- Header -->
        <div class="briefing-header">
          <span class="briefing-title font-display">{{ STRINGS.tutorial.briefing.title }}</span>
          <button
            class="briefing-skip"
            @click="onSkip"
            :aria-label="STRINGS.tutorial.briefing.skip"
          >
            {{ STRINGS.tutorial.briefing.skip }}
          </button>
        </div>

        <!-- Progress dots -->
        <div
          class="briefing-dots"
          role="tablist"
          :aria-label="STRINGS.tutorial.briefing.stepOf(currentCard + 1, cards.length)"
        >
          <button
            v-for="(_, i) in cards"
            :key="i"
            class="briefing-dot"
            :class="{
              'briefing-dot--active': i === currentCard,
              'briefing-dot--done': i < currentCard,
            }"
            role="tab"
            :aria-selected="i === currentCard"
            :aria-label="`Card ${i + 1} of ${cards.length}`"
            @click="currentCard = i"
          />
        </div>

        <!-- Card area -->
        <div class="briefing-cards" @touchstart="onTouchStart" @touchend="onTouchEnd">
          <Transition :name="isReduced ? '' : 'card-slide'" mode="out-in">
            <div :key="currentCard" class="briefing-card" role="tabpanel">
              <div class="briefing-card-icon" aria-hidden="true">
                <AppIcon
                  :name="cardIconNames[currentCard]"
                  :size="40"
                  color="var(--color-energy-primary)"
                />
              </div>
              <h2 class="briefing-card-heading font-display">{{ cards[currentCard].heading }}</h2>
              <ul class="briefing-card-bullets">
                <li v-for="bullet in cards[currentCard].bullets" :key="bullet">{{ bullet }}</li>
              </ul>
            </div>
          </Transition>
        </div>

        <!-- Footer nav -->
        <div class="briefing-footer">
          <button v-if="currentCard > 0" class="briefing-back" @click="currentCard--">
            ← BACK
          </button>
          <span v-else class="briefing-back-spacer" />

          <span class="briefing-step-label">
            {{ STRINGS.tutorial.briefing.stepOf(currentCard + 1, cards.length) }}
          </span>

          <button
            class="briefing-next"
            :class="{ 'briefing-next--done': currentCard === cards.length - 1 }"
            @click="onNext"
          >
            {{
              currentCard === cards.length - 1
                ? STRINGS.tutorial.briefing.done
                : STRINGS.tutorial.briefing.next
            }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import STRINGS from '@/constants/strings';
import AppIcon from '@/components/ui/icons/AppIcon.vue';
import type { IconName } from '@/components/ui/icons/icon-paths';

interface Props {
  visible: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  complete: [];
}>();

const cards = STRINGS.tutorial.briefing.cards;
const cardIconNames: IconName[] = [
  'letter-grid',
  'word-search',
  'stopwatch',
  'clipboard',
  'trophy',
];

const currentCard = ref(0);

const isReduced = computed(() => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
});

let touchStartX = 0;

function onTouchStart(e: TouchEvent) {
  touchStartX = e.touches[0]?.clientX ?? 0;
}

function onTouchEnd(e: TouchEvent) {
  const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX;
  if (Math.abs(dx) < 40) return;
  if (dx < 0 && currentCard.value < cards.length - 1) {
    currentCard.value++;
  } else if (dx > 0 && currentCard.value > 0) {
    currentCard.value--;
  }
}

function onNext() {
  if (currentCard.value < cards.length - 1) {
    currentCard.value++;
  } else {
    emit('complete');
  }
}

function onSkip() {
  emit('complete');
}
</script>

<style scoped>
.briefing-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(10, 10, 15, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.briefing-panel {
  background: var(--color-arena-surface, #1a1612);
  border: 2px solid var(--color-arena-border, #3d3428);
  border-radius: 1rem;
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 0;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(201, 168, 76, 0.1);
  overflow: hidden;
}

.briefing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid var(--color-arena-border, #3d3428);
}

.briefing-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-energy-primary, #c9a84c);
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.briefing-skip {
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted, #8a7e6a);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: color 150ms;
}

.briefing-skip:hover {
  color: var(--color-arena-text-bright, #f5e6c8);
}

.briefing-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 0.5rem;
}

.briefing-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: var(--color-arena-border, #3d3428);
  cursor: pointer;
  padding: 0;
  transition:
    background-color 200ms ease,
    width 200ms ease,
    border-radius 200ms ease;
}

.briefing-dot--active {
  background: var(--color-energy-primary, #c9a84c);
  width: 20px;
  border-radius: 4px;
}

.briefing-dot--done {
  background: rgba(201, 168, 76, 0.4);
}

.briefing-cards {
  flex: 1;
  min-height: 240px;
  display: flex;
  align-items: stretch;
  overflow: hidden;
}

.briefing-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem 1.75rem;
  gap: 0.75rem;
}

.briefing-card-icon {
  line-height: 1;
}

.briefing-card-heading {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--color-arena-text-bright, #f5e6c8);
  margin: 0;
  letter-spacing: 0.05em;
}

.briefing-card-bullets {
  font-size: 0.875rem;
  color: var(--color-arena-text-muted, #8a7e6a);
  line-height: 1.6;
  margin: 0;
  padding: 0 0 0 1.25rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 22rem;
}

.briefing-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid var(--color-arena-border, #3d3428);
  gap: 0.5rem;
}

.briefing-back,
.briefing-back-spacer {
  min-width: 4.5rem;
  font-size: 0.875rem;
}

.briefing-back {
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted, #8a7e6a);
  cursor: pointer;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  transition: color 150ms;
  text-align: left;
}

.briefing-back:hover {
  color: var(--color-arena-text-bright, #f5e6c8);
}

.briefing-step-label {
  font-size: 0.75rem;
  color: var(--color-arena-text-muted, #8a7e6a);
  flex: 1;
  text-align: center;
}

.briefing-next {
  min-width: 4.5rem;
  background: var(--color-energy-primary, #c9a84c);
  color: #1a1612;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.4rem 0.75rem;
  transition:
    background 150ms,
    transform 100ms;
  text-align: right;
}

.briefing-next:hover {
  background: #b89636;
  transform: translateY(-1px);
}

.briefing-next:active {
  transform: translateY(0);
}

.briefing-next--done {
  background: var(--color-energy-primary, #c9a84c);
  box-shadow: 0 0 12px rgba(201, 168, 76, 0.4);
}

/* Transitions */
.briefing-fade-enter-active,
.briefing-fade-leave-active {
  transition: opacity 250ms ease;
}

.briefing-fade-enter-from,
.briefing-fade-leave-to {
  opacity: 0;
}

.card-slide-enter-active,
.card-slide-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.card-slide-enter-from {
  opacity: 0;
  transform: translateX(24px);
}

.card-slide-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}

@media (prefers-reduced-motion: reduce) {
  .briefing-fade-enter-active,
  .briefing-fade-leave-active,
  .card-slide-enter-active,
  .card-slide-leave-active {
    transition: none;
  }
}
</style>
